/**
 * The comparison pages, the guides and the about page (articles.ts): the answer under the title, the key facts as a
 * table, then one section per question, each opening on its direct answer. Laid out like the legal pages.
 */
import { Link } from 'react-router';
import { cx } from '../components/ui';
import { CHECKED, compareNote, longDate, type ArticlePath, type Block, type Table } from './articles';
import { Band, ClosingCta, Eyebrow, SiteLayout } from './chrome';
import { article, PAGES } from './content';
import { useFacts } from './hooks';

export function ArticlePage({ path }: { path: ArticlePath }) {
  const page = PAGES[path];
  const meta = page.article!;
  const a = article(path, useFacts());
  const note = compareNote(path);
  return (
    <SiteLayout title={page.title}>
      <Band stage="soft" inner="!pt-14 lg:!pt-20">
        <div className="grid gap-12 lg:grid-cols-[260px_minmax(0,1fr)] lg:gap-16">
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <Eyebrow>{a.eyebrow}</Eyebrow>
            <h1 className="mt-5 font-display text-[clamp(36px,4vw,52px)] leading-[1] font-[580] tracking-[-0.04em]">{a.h1}</h1>
            <div className="site-eyebrow mt-5 text-site-muted">
              Updated <time dateTime={meta.updated}>{longDate(meta.updated)}</time>
            </div>
            <nav aria-label="On this page" className="mt-8 hidden max-h-[calc(100vh-18rem)] overflow-y-auto border-t border-site-rule pt-5 lg:block">
              <ol className="space-y-2 text-[14px]">
                {a.sections.map((s, i) => (
                  <li key={s.id}>
                    <a href={`#${s.id}`} className="flex gap-3 text-site-ink-2 hover:text-site-ink">
                      <span className="font-plex text-[12px] text-site-muted">{String(i + 1).padStart(2, '0')}</span>
                      {s.q}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </aside>
          {/* break-words: file paths and addresses wrap on a phone instead of running off the side. */}
          <article className="min-w-0 max-w-[76ch] break-words">
            <p className="text-[19px] leading-relaxed text-site-ink">{a.lead}</p>
            {note && <p className="mt-4 text-[14.5px] leading-relaxed text-site-muted">{note}</p>}
            {a.facts && (
              <div className="mt-8">
                <Facts table={a.facts} />
              </div>
            )}
            {a.sections.map((s, i) => (
              <section key={s.id} id={s.id} className="mt-12 scroll-mt-24 border-t border-site-rule pt-8">
                <h2 className="flex items-baseline gap-4 font-display text-[22px] font-[600] tracking-[-0.025em]">
                  <span className="font-plex text-[13px] font-normal text-site-muted">{String(i + 1).padStart(2, '0')}</span>
                  {s.q}
                </h2>
                <div className="mt-4 space-y-4 text-[16px] leading-[1.7] text-site-ink-2">
                  <p className="text-[17px] text-site-ink">{s.a}</p>
                  {s.more?.map((b, j) => <BlockView key={j} block={b} />)}
                </div>
              </section>
            ))}
            {a.sources && (
              <section className="mt-12 border-t border-site-rule pt-8 text-[15px] text-site-ink-2">
                <h2 className="site-eyebrow text-site-muted">Sources{meta.kind === 'compare' && <>, read {longDate(CHECKED)}</>}</h2>
                <ul className="mt-4 space-y-2">
                  {a.sources.map((s) => (
                    <li key={s.url}>
                      <a href={s.url} target="_blank" rel="noreferrer" className="text-site-green underline-offset-4 hover:underline">
                        {s.label}
                      </a>
                    </li>
                  ))}
                </ul>
                {meta.kind === 'compare' && (
                  <p className="mt-4 text-[14px] text-site-muted">
                    Products and prices change. If something here is out of date, <Link to="/contact" className="underline underline-offset-2 hover:text-site-ink">tell us</Link> and we will fix it.
                  </p>
                )}
              </section>
            )}
            <nav aria-label="Related" className="mt-12 border-t border-site-rule pt-8">
              <div className="site-eyebrow text-site-muted">Read next</div>
              <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                {a.related.map((p) => (
                  <li key={p}>
                    <Link to={p} className="block rounded-[14px] bg-site-oat px-4 py-3 text-[15px] font-[540] text-site-ink transition-colors hover:bg-site-oat-2">
                      {PAGES[p].name}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          </article>
        </div>
      </Band>
      <ClosingCta />
    </SiteLayout>
  );
}

function BlockView({ block }: { block: Block }) {
  if (typeof block === 'string') return <p>{block}</p>;
  if ('list' in block)
    return (
      <ul className="list-disc space-y-2 pl-5 [&_li]:pl-1">
        {block.list.map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ul>
    );
  if ('table' in block) return <Facts table={block.table} />;
  return (
    <pre className="overflow-x-auto rounded-[14px] bg-site-ink px-4 py-3.5 font-plex text-[13px] leading-relaxed text-site-white/90">
      <code>{block.code}</code>
    </pre>
  );
}

/** A table whose first column reads as row headings. */
function Facts({ table }: { table: Table }) {
  return (
    <div className="overflow-x-auto rounded-[14px] ring-1 ring-site-rule">
      {/* Three columns or more keep a readable width on a phone and scroll sideways instead. */}
      <table className={cx('w-full border-collapse text-left text-[14.5px] leading-snug', (table.head ?? table.rows[0]!).length > 2 && 'min-w-[600px]')}>
        {table.head && (
          <thead>
            <tr className="bg-site-oat">
              {table.head.map((h, i) => (
                <th key={i} scope="col" className="site-eyebrow px-4 py-3 align-bottom font-normal text-site-muted">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {table.rows.map((r, i) => (
            <tr key={i} className={cx('align-top', (table.head || i > 0) && 'border-t border-site-rule')}>
              {r.map((c, j) =>
                j === 0 ? (
                  <th key={j} scope="row" className="px-4 py-3 font-[600] text-site-ink">
                    {c}
                  </th>
                ) : (
                  <td key={j} className="px-4 py-3 text-site-ink-2">
                    {c}
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
